import { useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Save, FileText, Download, Search, Image, Upload, Video, CheckSquare, ArrowLeft, ArrowRight, AlertCircle, CheckCircle, Clock, XCircle, Eye } from 'lucide-react';
import { cjmsApi } from '../../views/CJMSView/api/cjmsApi';

type CaseStudyStatus = 'draft' | 'review' | 'approved' | 'published';

interface CaseStudyData {
  customerName: string;
  businessName: string;
  industry: string;
  city: string;
  province: string;
  tier: 'FREE' | 'R197' | 'R497' | 'R997';
  duration: number;
  result: string;
  startingPoint: string;
  challenge: string;
  previousAttempts: string;
  approach: string;
  productsUsed: string;
  implementationTimeline: string;
  revenueGrowthBefore: number;
  revenueGrowthAfter: number;
  customerGrowthBefore: number;
  customerGrowthAfter: number;
  timeSaved: string;
  qualitativeOutcomes: string;
  testimonialQuote: string;
  testimonialAttribution: string;
  consentPublication: boolean;
  status: CaseStudyStatus;
}

const SA_INDUSTRIES = [
  'Accounting & Finance',
  'Architecture & Design',
  'Automotive',
  'Beauty & Wellness',
  'Construction & Trades',
  'Consulting & Coaching',
  'Education & Training',
  'Entertainment & Media',
  'Food & Beverage',
  'Health & Medical',
  'IT & Technology',
  'Legal Services',
  'Marketing & Advertising',
  'Photography & Videography',
  'Real Estate',
  'Retail & E-commerce',
  'Sports & Fitness',
  'Tourism & Hospitality',
  'Transportation & Logistics',
  'Manufacturing',
  'Agriculture',
  'Non-Profit',
  'Other',
];

const TIERS: CaseStudyData['tier'][] = ['FREE', 'R197', 'R497', 'R997'];
const STATUS_CONFIG: Record<CaseStudyStatus, { label: string; icon: typeof Clock; color: string; bg: string }> = {
  draft: { label: 'Draft', icon: Clock, color: 'text-slate-600', bg: 'bg-slate-100' },
  review: { label: 'In Review', icon: Eye, color: 'text-amber-600', bg: 'bg-amber-50' },
  approved: { label: 'Approved', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  published: { label: 'Published', icon: CheckSquare, color: 'text-blue-600', bg: 'bg-blue-50' },
};

const emptyData: CaseStudyData = {
  customerName: '',
  businessName: '',
  industry: '',
  city: '',
  province: '',
  tier: 'R197',
  duration: 3,
  result: '',
  startingPoint: '',
  challenge: '',
  previousAttempts: '',
  approach: '',
  productsUsed: '',
  implementationTimeline: '',
  revenueGrowthBefore: 0,
  revenueGrowthAfter: 0,
  customerGrowthBefore: 0,
  customerGrowthAfter: 0,
  timeSaved: '',
  qualitativeOutcomes: '',
  testimonialQuote: '',
  testimonialAttribution: '',
  consentPublication: false,
  status: 'draft',
};

function generateMarkdown(data: CaseStudyData): string {
  const revenuePct = data.revenueGrowthBefore > 0
    ? Math.round(((data.revenueGrowthAfter - data.revenueGrowthBefore) / data.revenueGrowthBefore) * 100)
    : 0;
  const customerPct = data.customerGrowthBefore > 0
    ? Math.round(((data.customerGrowthAfter - data.customerGrowthBefore) / data.customerGrowthBefore) * 100)
    : 0;

  return `# ${data.customerName} — From ${data.startingPoint || 'Starting Point'} to ${data.result || 'Result'}

**Customer**: ${data.customerName}, ${data.businessName}
**Industry**: ${data.industry}
**Location**: ${data.city}, ${data.province}
**Tier**: ${data.tier}
**Duration**: ${data.duration} months
**Result**: ${data.result}

---

## The Challenge

${data.startingPoint}

${data.challenge}

${data.previousAttempts ? `**Previous attempts**: ${data.previousAttempts}` : ''}

---

## The Solution

${data.approach}

**Products/Services used**: ${data.productsUsed}

**Implementation timeline**: ${data.implementationTimeline}

---

## The Results

Within ${data.duration} months of joining BizBuilder:

**Quantitative Results:**
${data.revenueGrowthBefore > 0 || data.revenueGrowthAfter > 0 ? `- Revenue: R${data.revenueGrowthBefore.toLocaleString()}/month → R${data.revenueGrowthAfter.toLocaleString()}/month (${revenuePct}% growth)` : ''}
${data.customerGrowthBefore > 0 || data.customerGrowthAfter > 0 ? `- Customers: ${data.customerGrowthBefore} → ${data.customerGrowthAfter} (${customerPct}% increase)` : ''}
${data.timeSaved ? `- Time saved: ${data.timeSaved}` : ''}

**Qualitative Outcomes:**
${data.qualitativeOutcomes}

---

## Testimonial

"${data.testimonialQuote}"

— ${data.testimonialAttribution}

---

## Key Takeaways

- **Start FREE**: The 1-Pager Website Campaign removed all barriers
- **Follow the System**: The 7 Laws framework provided clear structure
- **Consistency Wins**: The 10-a-Day Mantra created momentum
- **Community Matters**: Peer support kept motivation high
- **Upgrade When Ready**: Moving to ${data.tier}/mo accelerated growth

---

## Visuals

### Before/After
- [ ] Before: ${data.startingPoint || 'Starting point'}
- [ ] After: ${data.result || 'Result'}

### Metrics Dashboard
- [ ] Revenue chart (before/after)
- [ ] Customer growth chart
- [ ] Website traffic graph

### Photos
- [ ] Customer headshot (with permission)
- [ ] Business location (if applicable)
- [ ] Website screenshot
`;
}

export default function CaseStudyForm() {
  const [data, setData] = useState<CaseStudyData>(emptyData);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ name: string; email: string; company?: string }>>([]);
  const [searching, setSearching] = useState(false);
  const [photoUploads, setPhotoUploads] = useState<Record<string, string>>({});

  const updateField = useCallback(<K extends keyof CaseStudyData>(key: K, value: CaseStudyData[K]) => {
    setData(prev => ({ ...prev, [key]: value }));
    setSaved(false);
    setError(null);
  }, []);

  const handleClientSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setError(null);
    try {
      const clients = await cjmsApi.fetchClients();
      const q = searchQuery.toLowerCase().trim();
      const results = clients
        .filter(c => c.name.toLowerCase().includes(q) || (c.company && c.company.toLowerCase().includes(q)) || c.email.toLowerCase().includes(q))
        .slice(0, 10)
        .map(c => ({ name: c.name, email: c.email, company: c.company }));
      setSearchResults(results);
      if (results.length === 0) {
        setError('No matching clients found. Please check the name or add manually.');
      }
    } catch (err: any) {
      setError('Failed to search clients: ' + err.message);
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  const selectClient = useCallback((client: { name: string; email: string; company?: string }) => {
    setData(prev => ({ ...prev, customerName: client.name, businessName: client.company || prev.businessName }));
    setSearchQuery('');
    setSearchResults([]);
    setSaved(false);
  }, []);

  const handlePhotoUpload = useCallback((section: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          setPhotoUploads(prev => ({ ...prev, [section]: ev.target?.result as string }));
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await new Promise(r => setTimeout(r, 500));
      setSaved(true);
    } catch (err: any) {
      setError('Failed to save case study: ' + err.message);
    } finally {
      setSaving(false);
    }
  }, [data]);

  const handleExportMarkdown = useCallback(() => {
    const markdown = generateMarkdown(data);
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `case-study-${data.customerName.replace(/\s+/g, '-').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  const advanceStatus = useCallback(() => {
    const order: CaseStudyStatus[] = ['draft', 'review', 'approved', 'published'];
    const idx = order.indexOf(data.status);
    if (idx < order.length - 1) {
      setData(prev => ({ ...prev, status: order[idx + 1] }));
    }
  }, [data.status]);

  const requiredFields = useMemo(() => {
    const missing: string[] = [];
    if (!data.customerName) missing.push('Customer name');
    if (!data.businessName) missing.push('Business name');
    if (!data.industry) missing.push('Industry');
    if (!data.city) missing.push('City');
    if (!data.province) missing.push('Province');
    if (!data.result) missing.push('Result');
    if (!data.startingPoint) missing.push('Starting point/Background');
    if (!data.challenge) missing.push('Challenge description');
    if (!data.approach) missing.push('Solution approach');
    if (!data.productsUsed) missing.push('Products/services used');
    if (data.testimonialQuote.length > 0 && !data.testimonialAttribution) missing.push('Testimonial attribution');
    return missing;
  }, [data]);

  const StatusBadge = STATUS_CONFIG[data.status];
  const canAdvance = data.status !== 'published' && requiredFields.length === 0 && data.consentPublication;
  const sections = ['Header & Background', 'Solution', 'Results', 'Testimonial & Media', 'Review & Export'];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-white rounded-[2rem] border border-slate-200 p-6 lg:p-8">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="bg-amber-100 p-3 rounded-2xl text-amber-700">
                <FileText size={24} />
              </div>
              <div>
                <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">Case Study Form</h2>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Create customer success stories</p>
              </div>
            </div>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${StatusBadge.bg}`}>
            <StatusBadge.icon size={14} className={StatusBadge.color} />
            <span className={`text-[10px] font-black uppercase tracking-widest ${StatusBadge.color}`}>{StatusBadge.label}</span>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-700">
            <AlertCircle size={14} />
            {error}
          </div>
        )}

        {saved && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2 text-sm text-green-700">
            <CheckCircle size={14} />
            Case study saved successfully
          </div>
        )}

        {requiredFields.length > 0 && step !== 4 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 mb-1">Missing Required Fields</p>
            <div className="flex flex-wrap gap-2">
              {requiredFields.map(f => (
                <span key={f} className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded-lg">{f}</span>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 overflow-x-auto pb-4 mb-6 border-b border-slate-100">
          {sections.map((s, i) => (
            <button
              key={s}
              onClick={() => setStep(i)}
              className={`shrink-0 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                step === i ? 'bg-amber-100 text-amber-700' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {step === 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-700">Customer Lookup</h3>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleClientSearch()}
                  placeholder="Search existing CJMS clients..."
                  className="w-full pl-9 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 outline-none"
                />
              </div>
              <button
                onClick={handleClientSearch}
                disabled={searching || !searchQuery.trim()}
                className="px-4 py-3 bg-amber-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-700 disabled:bg-slate-300 transition-colors"
              >
                {searching ? '...' : 'Search'}
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                {searchResults.map((r, i) => (
                  <button
                    key={r.email}
                    onClick={() => selectClient(r)}
                    className={`w-full text-left px-4 py-3 hover:bg-amber-50 transition-colors flex items-center justify-between ${
                      i < searchResults.length - 1 ? 'border-b border-slate-100' : ''
                    }`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{r.name}</p>
                      <p className="text-xs text-slate-500">{r.email}{r.company ? ` — ${r.company}` : ''}</p>
                    </div>
                    <CheckCircle size={16} className="text-amber-600" />
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Customer Name *</label>
                <input type="text" value={data.customerName} onChange={e => updateField('customerName', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Business Name *</label>
                <input type="text" value={data.businessName} onChange={e => updateField('businessName', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Industry *</label>
                <select value={data.industry} onChange={e => updateField('industry', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none">
                  <option value="">Select industry...</option>
                  {SA_INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">City *</label>
                  <input type="text" value={data.city} onChange={e => updateField('city', e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Province *</label>
                  <input type="text" value={data.province} onChange={e => updateField('province', e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Tier</label>
                  <select value={data.tier} onChange={e => updateField('tier', e.target.value as CaseStudyData['tier'])}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none">
                    {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Duration (mo)</label>
                  <input type="number" min={1} max={60} value={data.duration} onChange={e => updateField('duration', parseInt(e.target.value) || 3)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Result *</label>
                  <input type="text" value={data.result} onChange={e => updateField('result', e.target.value)} placeholder="e.g. 3x revenue"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Starting Point / Background *</label>
              <textarea value={data.startingPoint} onChange={e => updateField('startingPoint', e.target.value)} rows={3}
                placeholder="Describe the customer's business situation before BizBuilder..."
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Challenge *</label>
              <textarea value={data.challenge} onChange={e => updateField('challenge', e.target.value)} rows={3}
                placeholder="What specific challenges were they facing?"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Previous Attempts</label>
              <textarea value={data.previousAttempts} onChange={e => updateField('previousAttempts', e.target.value)} rows={2}
                placeholder="What had they tried before? (optional)"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            </div>
          </motion.div>
        )}

        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-700">Solution Details</h3>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Approach *</label>
              <textarea value={data.approach} onChange={e => updateField('approach', e.target.value)} rows={4}
                placeholder="How did the customer use BizBuilder to solve their problem?"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Products/Services Used *</label>
              <input type="text" value={data.productsUsed} onChange={e => updateField('productsUsed', e.target.value)}
                placeholder="e.g. 1-Pager Website, Goal-to-100 Program, 10-a-Day Mantra"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Implementation Timeline</label>
              <textarea value={data.implementationTimeline} onChange={e => updateField('implementationTimeline', e.target.value)} rows={3}
                placeholder="Describe the timeline of their BizBuilder journey..."
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-700">Results — Before / After Comparison</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-[10px] font-black uppercase tracking-widest text-red-700 mb-3">Before</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Monthly Revenue (R)</label>
                    <input type="number" min={0} value={data.revenueGrowthBefore || ''} onChange={e => updateField('revenueGrowthBefore', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 bg-white border border-red-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-500/30" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Customer Count</label>
                    <input type="number" min={0} value={data.customerGrowthBefore || ''} onChange={e => updateField('customerGrowthBefore', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 bg-white border border-red-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-500/30" />
                  </div>
                </div>
              </div>
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-[10px] font-black uppercase tracking-widest text-green-700 mb-3">After</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Monthly Revenue (R)</label>
                    <input type="number" min={0} value={data.revenueGrowthAfter || ''} onChange={e => updateField('revenueGrowthAfter', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 bg-white border border-green-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-green-500/30" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Customer Count</label>
                    <input type="number" min={0} value={data.customerGrowthAfter || ''} onChange={e => updateField('customerGrowthAfter', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 bg-white border border-green-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-green-500/30" />
                  </div>
                </div>
              </div>
            </div>
            {data.revenueGrowthBefore > 0 && data.revenueGrowthAfter > 0 && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-center">
                <span className="text-[10px] font-black uppercase tracking-widest text-blue-700">Revenue Growth: </span>
                <span className="text-lg font-black text-blue-800">
                  {Math.round(((data.revenueGrowthAfter - data.revenueGrowthBefore) / data.revenueGrowthBefore) * 100)}%
                </span>
              </div>
            )}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Time Saved</label>
              <input type="text" value={data.timeSaved} onChange={e => updateField('timeSaved', e.target.value)}
                placeholder="e.g. 10 hours/week on admin"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Qualitative Outcomes</label>
              <textarea value={data.qualitativeOutcomes} onChange={e => updateField('qualitativeOutcomes', e.target.value)} rows={3}
                placeholder="Describe non-quantitative improvements..."
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-700">Testimonial & Media</h3>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Customer Quote</label>
              <textarea value={data.testimonialQuote} onChange={e => updateField('testimonialQuote', e.target.value)} rows={4}
                placeholder="Direct quote from the customer..."
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Attribution (if quote provided)</label>
              <input type="text" value={data.testimonialAttribution} onChange={e => updateField('testimonialAttribution', e.target.value)}
                placeholder="e.g. Thabo M., Owner, Thabo's Plumbing"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            </div>

            <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-600 mt-6">Photo/Media Uploads</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { key: 'customer', label: 'Customer Headshot' },
                { key: 'logo', label: 'Business Logo' },
                { key: 'screenshot', label: 'Website Screenshot' },
              ].map(item => (
                <div key={item.key} className="border border-dashed border-slate-300 rounded-xl p-4 text-center">
                  {photoUploads[item.key] ? (
                    <div className="space-y-2">
                      <img src={photoUploads[item.key]} alt={item.label} className="w-20 h-20 object-cover rounded-lg mx-auto" />
                      <button onClick={() => setPhotoUploads(prev => { const n = { ...prev }; delete n[item.key]; return n; })}
                        className="text-[10px] text-red-500 font-black uppercase tracking-widest">Remove</button>
                    </div>
                  ) : (
                    <button onClick={() => handlePhotoUpload(item.key)} className="w-full space-y-2">
                      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto">
                        <Upload size={20} className="text-slate-400" />
                      </div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{item.label}</p>
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Video Testimonial Link</label>
              <div className="flex items-center gap-2">
                <Video size={16} className="text-slate-400" />
                <input type="url" placeholder="https://..."
                  className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
              </div>
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={data.consentPublication}
                  onChange={e => updateField('consentPublication', e.target.checked)}
                  className="mt-0.5 rounded border-slate-300 text-amber-600 focus:ring-amber-500" />
                <div>
                  <p className="text-xs font-bold text-amber-800">Publication Consent *</p>
                  <p className="text-[10px] text-amber-700 mt-1">
                    I, {data.customerName || '[Customer_Name]'}, give Chrysalinks Group permission to use my name, business name, and success metrics in marketing materials. I understand I can revoke this consent at any time in compliance with POPIA.
                  </p>
                </div>
              </label>
            </div>
          </motion.div>
        )}

        {step === 4 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-700">Review & Export</h3>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-600">Approval Workflow</h4>
                <div className="flex items-center gap-2">
                  {(['draft', 'review', 'approved', 'published'] as const).map((s, i) => (
                    <div key={s} className="flex items-center gap-1">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        ['draft', 'review', 'approved', 'published'].indexOf(data.status) >= i
                          ? 'bg-amber-600 text-white'
                          : 'bg-slate-200 text-slate-400'
                      }`}>
                        <span className="text-[8px] font-black">{i + 1}</span>
                      </div>
                      {i < 3 && <div className={`w-4 h-0.5 ${['draft', 'review', 'approved', 'published'].indexOf(data.status) > i ? 'bg-amber-600' : 'bg-slate-200'}`} />}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                  data.status === 'draft' ? 'bg-slate-200 text-slate-600' :
                  data.status === 'review' ? 'bg-amber-100 text-amber-700' :
                  data.status === 'approved' ? 'bg-green-100 text-green-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  Status: {StatusBadge.label}
                </div>
                <div className="flex gap-2">
                  {data.status === 'draft' && (
                    <button onClick={() => updateField('status', 'review')}
                      disabled={!canAdvance}
                      className="px-4 py-2 bg-amber-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-700 disabled:bg-slate-300 transition-colors">
                      Submit for Review
                    </button>
                  )}
                  {data.status === 'review' && (
                    <div className="flex gap-2">
                      <button onClick={() => updateField('status', 'approved')}
                        className="px-4 py-2 bg-green-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-green-700 transition-colors">
                        Approve
                      </button>
                      <button onClick={() => updateField('status', 'draft')}
                        className="px-4 py-2 bg-red-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-700 transition-colors">
                        Send Back
                      </button>
                    </div>
                  )}
                  {data.status === 'approved' && (
                    <button onClick={() => updateField('status', 'published')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-colors">
                      Publish
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-3">Markdown Preview</h4>
              <pre className="text-xs text-slate-700 whitespace-pre-wrap bg-white border border-slate-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                {generateMarkdown(data)}
              </pre>
            </div>

            <div className="flex gap-3">
              <button onClick={handleExportMarkdown}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-700 transition-colors">
                <Download size={16} />
                Export Markdown
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 disabled:bg-slate-300 transition-colors">
                <Save size={16} />
                {saving ? 'Saving...' : saved ? 'Saved' : 'Save Case Study'}
              </button>
            </div>
          </motion.div>
        )}

        <div className="flex justify-between mt-6 pt-6 border-t border-slate-100">
          <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}
            className="flex items-center gap-2 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-800 disabled:opacity-30 transition-colors">
            <ArrowLeft size={14} />
            Previous
          </button>
          <button onClick={() => setStep(s => Math.min(sections.length - 1, s + 1))} disabled={step === sections.length - 1}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-200 disabled:opacity-30 transition-colors">
            Next
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
